import { Component, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { DelegationService } from '../../../core/services/delegation-service/delegation.service';
import { Router } from '@angular/router';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { constantTxt } from '../../../core/constants/constants';
import { SessionStorageService } from '../../../core/services/session-storage';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-delegation-add',
    imports: [
        CommonModule,
        ReactiveFormsModule,
        MatDatepickerModule,
        MatInputModule,
        MatFormFieldModule,
        NzSelectModule,
        NzGridModule,
        NzDatePickerModule,
        NzButtonModule,
    ],
    templateUrl: './delegation-add.component.html',
    styleUrls: ['./delegation-add.component.less']
})
export class DelegationAddComponent implements OnInit, OnDestroy {
  delegationForm!: FormGroup;
  delegateOptions: { name: string; email: string }[] = [];
  isSubmitted = false;
  loggedInUserName: string = '';
  loggedInUserEmail: string = '';
  selectedDelegateeName: string = '';
  existingDelegations: {
    delFrom: string;
    delThrough: string;
    delDateFrom: string;
    delDateThrough: string;
  }[] = [];
  constants = constantTxt;
  getDelegatesSubscription!: Subscription;
  searchDelegationsSubscription!: Subscription;
  saveDelegationSubscription!: Subscription;

  constructor(
    private readonly fb: FormBuilder,
    private readonly delegationService: DelegationService,
    private readonly router: Router,
    private readonly session: SessionStorageService
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.loadDelegationData();
    this.fetchLoggedInUser();
    this.loadExistingDelegations();
  }

  initForm(): void {
    this.delegationForm = this.fb.group({
      delDateFrom: [''],
      delDateThrough: [''],
      delFrom: [''],
      delThrough: [''],
    });
  }

  loadDelegationData(): void {
    this.getDelegatesSubscription = this.delegationService
      .getDelegates()
      .subscribe({
        next: (res: any) => {
          let data = res.body;
          if (data) {
            this.delegateOptions = data.map((item: any) => ({
              name: item.name,
              email: item.cai || item.email,
            }));
          }
        },
        error: (err: any) => {
          console.error('Error loading delegations:', err);
        },
      });
  }

  loadExistingDelegations(): void {
    this.searchDelegationsSubscription = this.delegationService
      .searchDelegations()
      .subscribe({
        next: (res: any) => {
          let data = res.body;
          if (data) {
            this.existingDelegations = data.map((d: any) => ({
              delFrom: d.delFrom,
              delThrough: d.delThrough,
              delDateFrom: d.dateFrom,
              delDateThrough: d.dateThrough,
            }));
          }
        },
        error: (err: any) => {
          console.error('Error loading existing delegations:', err);
        },
      });
  }

  fetchLoggedInUser(): void {
    const storedUser = this.session.getItem('UserName');
    const storedEmail = this.session.getItem('UserEmail');
    if (storedUser && storedEmail) {
      this.loggedInUserName = storedUser;
      this.loggedInUserEmail = storedEmail;
      this.delegationForm.patchValue({ delFrom: storedEmail });
    }
  }

  isOverlapping(
    newFrom: string,
    newThrough: string,
    delFrom: string,
    delThrough: string
  ): boolean {
    const newStart = new Date(newFrom);
    const newEnd = new Date(newThrough);
    return this.existingDelegations.some(
      (d) =>
        d.delFrom === delFrom &&
        d.delThrough === delThrough &&
        new Date(d.delDateFrom) < newEnd &&
        new Date(d.delDateThrough) > newStart
    );
  }

  onSubmit(): void {
    const controls = this.delegationForm.controls;

    const validation = (controlName: any): boolean => {
      if (!controls[controlName].value) {
        controls[controlName].setValidators([Validators.required]);
        controls[controlName].markAsDirty();
        controls[controlName].updateValueAndValidity({ onlySelf: true });
        return false;
      }
      return true;
    };

    if (!validation('delDateFrom')) {
      alert(`You didn't select Period From !!!`);
    } else if (!validation('delDateThrough')) {
      alert(`You didn't select Period Through !!!`);
    } else if (!validation('delThrough')) {
      alert(`You didn't enter Delegatee Name !!!`);
    } else {
      const delFrom = this.loggedInUserEmail;
      const delThrough = this.delegationForm.value.delThrough;
      const delDateFrom = this.delegationForm.value.delDateFrom;
      const delDateThrough = this.delegationForm.value.delDateThrough;
      const delDateFromDate = new Date(delDateFrom)?.toISOString();
      const delDateThroughDate = new Date(delDateThrough)?.toISOString();
      if (
        this.isOverlapping(delDateFrom, delDateThrough, delFrom, delThrough)
      ) {
        alert(
          'Delegation already exists for this user and period. Please check that your new date range does not overlap with an existing delegation for the same users.'
        );
        this.isSubmitted = false;
        return;
      }

      if (confirm('Are you sure the Delegation Information is correct?')) {
        this.saveDelegation(
          delDateFromDate,
          delDateThroughDate,
          delFrom,
          delThrough
        );
      }
    }
  }

  saveDelegation(
    dateFrom: string,
    dateThrough: string,
    delFrom: string,
    delThrough: string
  ) {
    const selectedDelegate = this.delegateOptions.find(
      (user) => user.email === delThrough
    );
    this.selectedDelegateeName = selectedDelegate?.name || '';

    const payload = {
      delDateFrom: dateFrom,
      delDateThrough: dateThrough,
      delFrom: delFrom,
      delThrough: delThrough,
      delBy: this.loggedInUserEmail,
      delegateeName: this.selectedDelegateeName,
    };

    this.saveDelegationSubscription = this.delegationService
      .saveDelegation(payload)
      .subscribe({
        next: () => {
          alert('Delegation saved successfully!');
          this.delegationForm.reset();
          this.isSubmitted = false;
          this.router.navigate(['/delegation/delegation-list']); // <-- Add this line to redirect
        },
        error: (err) => {
          console.error('Error:', err);
          const backendMsg = err?.error?.message || err?.error;
          if (typeof backendMsg === 'string') {
            alert(backendMsg);
          } else {
            alert(
              'Delegation already exists for this user and period. Please check that your new date range does not overlap with an existing delegation for the same users.'
            );
          }
          this.isSubmitted = false;
        },
      });
  }

  backButton() {
    this.router.navigate(['/delegation/delegation-list']);
  }

  ngOnDestroy(): void {
    this.delegationForm.reset();

    if (this.getDelegatesSubscription) {
      this.getDelegatesSubscription.unsubscribe();
    }

    if (this.searchDelegationsSubscription) {
      this.searchDelegationsSubscription.unsubscribe();
    }

    if (this.saveDelegationSubscription) {
      this.saveDelegationSubscription.unsubscribe();
    }
  }
}
